import { Progress, SidebarItem } from "@backstage/core-components";
import { Link, Text } from "@backstage/ui";
import { AVD_GATE, type EntitlementGate } from "@internal/backstage-plugin-avd-common";
import DesktopWindowsOutlinedIcon from "@material-ui/icons/DesktopWindowsOutlined";
import SupervisorAccountOutlinedIcon from "@material-ui/icons/SupervisorAccountOutlined";
import { PropsWithChildren } from "react";

import { useEntitled, usePlatformAdmin } from "../identity/ownership";
import { PageHero, PageShell, StateCard } from "../layout/pageShell";

// Frontend entitlement — UX HIDING ONLY, not the security boundary.
//
// The real gate is the backend permission policy (packages/backend/src/permissionPolicy.ts), which
// DENIES each gate's actions server-side for non-members. This module just avoids rendering a gated
// surface (sidebar item, owner page) to users who can't use it — so they don't click through and fail
// late. Frontend hiding alone would be trivially bypassable; it's purely cleanliness layered on top.
//
// Entitled = member of the same groups the backend policy honours, via the same gate imported from
// the shared package — which is what stops the two from drifting apart.

/** A guarded page's hero text: its eyebrow is shared with its gate page, its title is what the gate shows. */
export type PageHeroText = { eyebrow: string; title: string };

export const MY_AVD_HERO: PageHeroText = {
	eyebrow: "My AVD · personal desktop",
	title: "My AVD",
};
/** The forms under My AVD; the template ids stay provision/deprovision (packages/backend/avd*-template.yaml). */
export const MY_AVD_SETUP_PATH = "/my-avd/setup";
export const MY_AVD_REMOVE_PATH = "/my-avd/remove";
export const ADMIN_HERO: PageHeroText = { eyebrow: "Admin · platform estate", title: "Admin" };

export type AvdEntitlement = { entitled: boolean; loading: boolean };

/** Reads the signed-in identity's group memberships to decide if AVD surfaces should show. */
export function useAvdEntitled(): AvdEntitlement {
	const { member, loading } = useEntitled(AVD_GATE);
	return { entitled: member, loading };
}

/** "My AVD" sidebar item — rendered only for entitled users (hidden while loading / unentitled). */
export const AvdSidebarItem = () => {
	const { entitled } = useAvdEntitled();
	return entitled ? (
		<SidebarItem icon={DesktopWindowsOutlinedIcon} to="my-avd" text="My AVD" />
	) : null;
};

/**
 * Route guard for a gated page: non-members get a polite "request access" card naming the gate's group
 * (covers deep-links), under the guarded page's own hero.
 */
export const RequireEntitled = ({
	gate,
	hero,
	what,
	children,
}: PropsWithChildren<{ gate: EntitlementGate; hero: PageHeroText; what: string }>) => {
	const { member, loading } = useEntitled(gate);
	if (loading) return <Progress />;
	if (member) return <>{children}</>;
	return (
		<PageShell>
			<PageHero {...hero} />
			<StateCard title="Access required">
				You don't currently have access to {what}. Access is granted on request — ask IT to
				add you to the <strong>{gate.groupDisplayName}</strong> group, then reload.
			</StateCard>
		</PageShell>
	);
};

/** Route guard for AVD pages, with My AVD's own hero. */
export const RequireAvdEntitled = ({ children }: PropsWithChildren<{}>) => (
	<RequireEntitled gate={AVD_GATE} hero={MY_AVD_HERO} what="personal cloud desktops">
		{children}
	</RequireEntitled>
);

/** "Admin" sidebar item — rendered only for platform admins (hidden while loading / for everyone else). */
export const AdminSidebarItem = () => {
	const { admin } = usePlatformAdmin();
	return admin ? (
		<SidebarItem icon={SupervisorAccountOutlinedIcon} to="admin" text="Admin" />
	) : null;
};

/**
 * Route guard for the admin page: non-admins get a plain "not for you" card rather than the
 * request-access card the entitlement guard shows.
 *
 * The copy difference is deliberate. `RequireEntitled` invites the reader to ASK for access,
 * because AVD is a self-service product any colleague may reasonably want. Platform admin is not
 * something to request through a portal card, so this states the fact and stops — pointing at the
 * page they actually want instead.
 *
 * This is UX hiding, exactly like the entitlement gate: the real boundary is the server-side group
 * check on `GET /bundles` in `plugins/avd-backend`. The page also handles being refused by it (a 403
 * means the two disagreed), which is what keeps the cosmetic layer honest.
 */
export const RequirePlatformAdmin = ({ children }: PropsWithChildren<{}>) => {
	const { admin, loading } = usePlatformAdmin();
	if (loading) return <Progress />;
	if (admin) return <>{children}</>;
	return (
		<PageShell>
			<PageHero {...ADMIN_HERO} />
			<StateCard
				title="Platform admins only"
				action={
					<Text as="p" color="secondary">
						Looking for your own desktop? It's on <Link href="/my-avd">My AVD</Link>.
					</Text>
				}
			>
				This page is the platform team's view of every colleague's bundles, and it's limited
				to members of <strong>Backstage Admins</strong>.
			</StateCard>
		</PageShell>
	);
};
